import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Download,
  ExternalLink,
  FileCheck2,
  HelpCircle,
  Loader2,
  Receipt,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import { getDocumentContentBlob } from "@/lib/api/documents";
import {
  useAddTaxPayment,
  useTaxEvent,
  useUpdateTaxEvent,
} from "@/lib/api/taxes.queries";
import type {
  TaxDeclarationRecord,
  TaxDueDateSource,
  TaxEventDetail,
  TaxEventStatus,
  TaxEvidenceSource,
  TaxFilingEvidenceInput,
  TaxPaymentBreakdown,
  TaxPaymentProof,
} from "@/lib/api/taxes";
import { useActiveCompany } from "@/lib/company-context";
import { fmtDate } from "@/components/inventory/format";
import { saveBlob } from "@/components/radian/format";
import { EventArrearsCard } from "@/components/taxes/TaxArrearsPanel";
import { TaxCorrectionSection } from "@/components/taxes/TaxCorrectionSection";
import { TaxDeclarationCard } from "@/components/taxes/TaxDeclarationCard";
import {
  TAX_STATUS_LABEL,
  TaxEventBadges,
} from "@/components/taxes/TaxEventBadges";
import { taxableYearOfPeriod } from "@/components/taxes/worklist";

export const Route = createFileRoute(
  "/companies/$companyId/impuestos/$eventId",
)({
  component: TaxEventPage,
});

const DUE_DATE_SOURCE_LABEL: Record<TaxDueDateSource, string> = {
  official_calendar: "calendario oficial DIAN",
  calendar_rule: "regla del calendario DIAN (Decreto 2229 de 2023)",
  estimated: "fecha estimada",
  accountant_override: "fecha del contador",
  manual: "fin del periodo",
};

const EVIDENCE_SOURCE_LABEL: Record<TaxEvidenceSource, string> = {
  muisca_import: "Importado de MUISCA",
  manual_upload: "Archivo subido",
  recorded_by_person: "Registrado a mano",
};

const PROOF_LABEL: Record<TaxPaymentProof, string> = {
  paid: "Pagado (sello bancario)",
  receipt_generated: "Recibo generado, pago sin probar",
};

/** Friendly titles for the API's filing and payment refusals (PF-388/PF-389). */
const TAX_ERROR_TITLE: Record<string, string> = {
  TAX_FILING_EVIDENCE_REQUIRED:
    "Falta la evidencia de la presentacion: numero de formulario y fecha de presentacion",
  TAX_FILING_DUPLICATE_FORM_NUMBER:
    "Ese numero de formulario ya esta registrado",
  TAX_PAYMENT_EVIDENCE_REQUIRED:
    "Un pago DIAN requiere el numero del recibo 490",
  TAX_FILING_NOT_APPROVED:
    "La declaracion necesita el visto bueno de un owner o admin",
  TAX_FILING_APPROVAL_STALE:
    "El visto bueno quedo desactualizado: los libros cambiaron",
  TAX_FILING_AMOUNT_MISMATCH:
    "El valor declarado no coincide con el valor aprobado",
  TAX_EVENT_NOT_FOUND: "El evento ya no existe",
};

function errorToast(fallback: string, err: unknown) {
  const code = err instanceof ApiError ? err.code : undefined;
  const title = (code && TAX_ERROR_TITLE[code]) || fallback;
  const description = err instanceof Error ? err.message : undefined;
  toast.error(title, {
    description: description !== title ? description : undefined,
  });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function isFiledStatus(status: TaxEventStatus): boolean {
  return status === "filed" || status === "paid";
}

type FilingDraft = {
  formNumber: string;
  filedAt: string;
  correctionOf: string;
};

const EMPTY_FILING: FilingDraft = {
  formNumber: "",
  filedAt: "",
  correctionOf: "",
};

/** The filing to send, or null when nothing was typed; `undefined` when the inputs are invalid. */
function filingFromDraft(
  draft: FilingDraft,
  event: Pick<TaxEventDetail, "formCode">,
): TaxFilingEvidenceInput | null | undefined {
  const formNumber = draft.formNumber.trim();
  if (!formNumber && !draft.filedAt && !draft.correctionOf.trim()) return null;
  if (!formNumber || !draft.filedAt) return undefined;
  return {
    formNumber,
    filedAt: draft.filedAt,
    formCode: event.formCode ?? undefined,
    correctionOf: draft.correctionOf.trim() || undefined,
  };
}

function TaxEventPage() {
  return (
    <RequireAuth>
      <AppShell>
        <TaxEventContent />
      </AppShell>
    </RequireAuth>
  );
}

function TaxEventContent() {
  const fmtCop = useMoneyFormatter();
  const { companyId, eventId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const eventQuery = useTaxEvent(companyId, eventId);
  const updateEvent = useUpdateTaxEvent(companyId, eventId);
  const addPayment = useAddTaxPayment(companyId, eventId);
  const event = eventQuery.data;

  const [status, setStatus] = useState<TaxEventStatus>("projected");
  const [dueDate, setDueDate] = useState("");
  const [declaredAmount, setDeclaredAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [statusFiling, setStatusFiling] = useState<FilingDraft>(EMPTY_FILING);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso);
  const [paymentReference, setPaymentReference] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [proof, setProof] = useState<TaxPaymentProof>("paid");
  const [breakdown, setBreakdown] = useState({
    impuesto: "",
    sancion: "",
    intereses: "",
  });
  const [paymentFiling, setPaymentFiling] = useState<FilingDraft>(EMPTY_FILING);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!event) return;
    setStatus(event.status);
    setDueDate(event.dueDate);
    setDeclaredAmount(
      event.declaredAmount === null ? "" : String(event.declaredAmount),
    );
    setNotes(event.notes ?? "");
    setPaymentAmount(
      String(Math.max(0, event.payableAmount - event.paidAmount)),
    );
  }, [event]);

  const isDian = event?.authority === "dian";
  // The filing inputs: required when the status leaves an open DIAN event
  // filed or paid; optional on an event already filed (a correction or the
  // form behind a legacy filing).
  const statusFilesEvent =
    !!event && isDian && isFiledStatus(status) && !isFiledStatus(event.status);
  const showStatusFiling = !!event && isDian && isFiledStatus(status);
  // A payment on an unfiled DIAN event files it too (PF-389).
  const paymentFilesEvent = !!event && isDian && !isFiledStatus(event.status);

  function saveOverride(e: React.FormEvent) {
    e.preventDefault();
    if (!event) return;
    let filing: TaxFilingEvidenceInput | undefined;
    if (showStatusFiling) {
      const parsed = filingFromDraft(statusFiling, event);
      if (parsed === undefined || (statusFilesEvent && parsed === null)) {
        toast.error(
          "Indica el numero de formulario y la fecha de presentacion",
          {
            description:
              "Registrar una declaracion DIAN como presentada requiere la evidencia tal como aparece en el formulario recibido por la DIAN.",
          },
        );
        return;
      }
      filing = parsed ?? undefined;
    }
    updateEvent.mutate(
      {
        // Send the status and the due date only when they changed: saving
        // other fields must not rewrite them.
        status: status !== event.status ? status : undefined,
        dueDate: dueDate !== event.dueDate ? dueDate : undefined,
        declaredAmount: declaredAmount === "" ? null : Number(declaredAmount),
        notes: notes.trim() || null,
        filing,
      },
      {
        onSuccess: () => {
          toast.success("Evento actualizado");
          setStatusFiling(EMPTY_FILING);
        },
        onError: (err) => errorToast("No pudimos actualizar el evento", err),
      },
    );
  }

  function parseBreakdown(): TaxPaymentBreakdown | null | undefined {
    const values = [
      breakdown.impuesto,
      breakdown.sancion,
      breakdown.intereses,
    ].map((value) => value.trim());
    if (values.every((value) => value === "")) return null;
    const numbers = values.map((value) => (value === "" ? 0 : Number(value)));
    if (numbers.some((value) => !Number.isFinite(value) || value < 0))
      return undefined;
    return {
      impuesto: numbers[0]!,
      sancion: numbers[1]!,
      intereses: numbers[2]!,
    };
  }

  function savePayment(e: React.FormEvent) {
    e.preventDefault();
    if (!event) return;
    const amount = Number(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Ingresa un valor de pago valido");
      return;
    }
    const receipt = receiptNumber.trim();
    if (isDian && !receipt) {
      toast.error(
        "Indica el numero del recibo oficial de pago (formulario 490)",
      );
      return;
    }
    const parsedBreakdown = parseBreakdown();
    if (parsedBreakdown === undefined) {
      toast.error("El desglose del recibo tiene valores invalidos");
      return;
    }
    let filing: TaxFilingEvidenceInput | undefined;
    if (paymentFilesEvent) {
      const parsed = filingFromDraft(paymentFiling, event);
      if (parsed === undefined || parsed === null) {
        toast.error(
          "Indica el numero de formulario y la fecha de presentacion",
          {
            description:
              "El pago de una declaracion DIAN aun no presentada tambien registra la presentacion.",
          },
        );
        return;
      }
      filing = parsed;
    }
    addPayment.mutate(
      {
        amount,
        paidAt: paymentDate,
        reference: paymentReference.trim() || null,
        receiptNumber: receipt || null,
        proof,
        breakdown: parsedBreakdown,
        filing,
      },
      {
        onSuccess: () => {
          toast.success(
            proof === "paid"
              ? "Pago registrado"
              : "Recibo registrado; el pago queda sin probar",
          );
          setPaymentReference("");
          setReceiptNumber("");
          setBreakdown({ impuesto: "", sancion: "", intereses: "" });
          setPaymentFiling(EMPTY_FILING);
        },
        onError: (err) => errorToast("No pudimos registrar el pago", err),
      },
    );
  }

  if (eventQuery.isPending) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col gap-6 p-6">
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  if (eventQuery.isError || !event) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <ErrorState
          title="No pudimos cargar el evento"
          description={eventQuery.error?.message ?? ""}
          onRetry={() => void eventQuery.refetch()}
          retryLabel="Reintentar"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId/impuestos"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Volver a impuestos
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Receipt className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">
              {event.displayName}
            </h1>
            <TaxEventBadges event={event} />
          </div>
          <p className="text-sm text-muted-foreground">
            Periodo {event.period.label} · vence {fmtDate(event.dueDate)}
            {` (${DUE_DATE_SOURCE_LABEL[event.dueDateSource]})`}
            {event.formCode ? ` · formulario ${event.formCode}` : ""}
          </p>
        </div>
      </header>

      <section className="grid gap-3 md:grid-cols-4">
        <Metric title="Proyectado" value={fmtCop(event.projectedAmount)} />
        <Metric title="Declarado" value={fmtCop(event.declaredAmount)} />
        <Metric title="Pagado" value={fmtCop(event.paidAmount)} />
        <Metric
          title="Saldo"
          value={fmtCop(Math.max(0, event.payableAmount - event.paidAmount))}
        />
      </section>

      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <main className="space-y-6">
          <EventArrearsCard
            companyId={companyId}
            eventId={event.id}
            taxableYear={taxableYearOfPeriod(event.period)}
            enabled={
              event.risk === "overdue" &&
              event.status !== "paid" &&
              event.status !== "not_applicable"
            }
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                De donde sale el valor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {event.projection.explanation}
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fuente</TableHead>
                    <TableHead>Detalle</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {event.projection.sourceBreakdown.map((source) => (
                    <TableRow key={source.key}>
                      <TableCell className="font-medium">
                        {source.label}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {source.detail}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtCop(source.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <HelpCircle className="h-4 w-4 text-primary" />
                Ayuda para entender y pagar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <h2 className="text-sm font-semibold">Que es</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {event.help.summary}
                </p>
              </div>
              <div>
                <h2 className="text-sm font-semibold">Por que aplica</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {event.help.whyApplies}
                </p>
              </div>
              <HelpList
                title="De donde viene"
                items={event.help.whereComesFrom}
              />
              <HelpList title="Como se paga" items={event.help.howToPay} />
              {event.help.forms.length ? (
                <div>
                  <h2 className="text-sm font-semibold">Formularios</h2>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {event.help.forms.map((form) => (
                      <Badge key={form} variant="outline">
                        {form}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}
              {event.help.officialLinks.length ? (
                <div>
                  <h2 className="text-sm font-semibold">Fuentes oficiales</h2>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {event.help.officialLinks.map((link) => (
                      <Button
                        key={link.url}
                        asChild
                        variant="outline"
                        size="sm"
                      >
                        <a href={link.url} target="_blank" rel="noreferrer">
                          {link.label}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    ))}
                  </div>
                </div>
              ) : null}
              <Alert variant="warning">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Confirmacion contable</AlertTitle>
                <AlertDescription>{event.help.caution}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileCheck2 className="h-4 w-4 text-primary" />
                Evidencia de presentacion
              </CardTitle>
            </CardHeader>
            <CardContent>
              {event.filings.length ? (
                <FilingsTable companyId={companyId} filings={event.filings} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {isDian
                    ? "Aun no hay formularios registrados. Al marcar la declaracion como presentada, registra el numero de formulario y la fecha tal como aparecen en el formulario recibido por la DIAN, o importa el original desde MUISCA."
                    : "Aun no hay formularios registrados para este evento."}
                </p>
              )}
            </CardContent>
          </Card>

          {isFiledStatus(event.status) && event.filings.length ? (
            <TaxCorrectionSection
              companyId={companyId}
              filings={event.filings}
            />
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pagos registrados</CardTitle>
            </CardHeader>
            <CardContent>
              {event.payments.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Recibo 490</TableHead>
                      <TableHead>Soporte</TableHead>
                      <TableHead>Referencia</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {event.payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{fmtDate(payment.paidAt)}</TableCell>
                        <TableCell>
                          {payment.receiptNumber ?? "—"}
                          {payment.breakdown ? (
                            <div className="text-xs text-muted-foreground">
                              impuesto {fmtCop(payment.breakdown.impuesto)} ·
                              sancion {fmtCop(payment.breakdown.sancion)} ·
                              intereses {fmtCop(payment.breakdown.intereses)}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              payment.proof === "paid" ? "success" : "warning"
                            }
                          >
                            {payment.proof === "paid"
                              ? "Pagado"
                              : "Recibo generado"}
                          </Badge>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {EVIDENCE_SOURCE_LABEL[payment.source]}
                          </div>
                        </TableCell>
                        <TableCell>
                          {payment.reference ||
                            payment.method ||
                            "Sin referencia"}
                        </TableCell>
                        <TableCell className="text-right">
                          {fmtCop(payment.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Aun no hay pagos registrados para este evento.
                </p>
              )}
            </CardContent>
          </Card>
        </main>

        <aside className="space-y-6">
          {event.authority === "dian" ? (
            <TaxDeclarationCard companyId={companyId} eventId={event.id} />
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Actualizar evento</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="grid gap-4" onSubmit={saveOverride}>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-status">Estado</Label>
                  <Select
                    value={status}
                    onValueChange={(value) =>
                      setStatus(value as TaxEventStatus)
                    }
                  >
                    <SelectTrigger id="tax-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TAX_STATUS_LABEL).map(
                        ([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {showStatusFiling ? (
                  <FilingFields
                    idPrefix="tax-status-filing"
                    draft={statusFiling}
                    onChange={setStatusFiling}
                    required={statusFilesEvent}
                    hint={
                      statusFilesEvent
                        ? "Una declaracion DIAN queda presentada solo con el numero de formulario y la fecha de presentacion que aparecen en el formulario recibido por la DIAN."
                        : "Opcional: registra una correccion o el formulario de una presentacion anterior."
                    }
                  />
                ) : null}
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-due">Fecha de vencimiento</Label>
                  <Input
                    id="tax-due"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-declared">Valor declarado</Label>
                  <Input
                    id="tax-declared"
                    inputMode="decimal"
                    value={declaredAmount}
                    onChange={(e) => setDeclaredAmount(e.target.value)}
                    placeholder={String(event.projectedAmount)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-notes">Notas</Label>
                  <textarea
                    id="tax-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-24 rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </div>
                <Button type="submit" disabled={updateEvent.isPending}>
                  <Save className="h-4 w-4" />
                  Guardar
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Registrar pago</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="grid gap-4" onSubmit={savePayment}>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-payment-amount">Valor pagado</Label>
                  <Input
                    id="tax-payment-amount"
                    inputMode="decimal"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-payment-date">Fecha</Label>
                  <Input
                    id="tax-payment-date"
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-payment-receipt">
                    Numero de recibo 490{isDian ? "" : " (opcional)"}
                  </Label>
                  <Input
                    id="tax-payment-receipt"
                    value={receiptNumber}
                    onChange={(e) => setReceiptNumber(e.target.value)}
                    placeholder="Numero del recibo oficial de pago"
                    required={isDian}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-payment-proof">Soporte</Label>
                  <Select
                    value={proof}
                    onValueChange={(value) =>
                      setProof(value as TaxPaymentProof)
                    }
                  >
                    <SelectTrigger id="tax-payment-proof">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(PROOF_LABEL) as TaxPaymentProof[]).map(
                        (value) => (
                          <SelectItem key={value} value={value}>
                            {PROOF_LABEL[value]}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  {proof === "receipt_generated" ? (
                    <p className="text-xs text-muted-foreground">
                      El evento queda presentado, nunca pagado, hasta probar el
                      pago.
                    </p>
                  ) : null}
                </div>
                <div className="grid gap-1.5">
                  <Label>Desglose del recibo (opcional)</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      aria-label="Impuesto"
                      inputMode="decimal"
                      value={breakdown.impuesto}
                      onChange={(e) =>
                        setBreakdown((b) => ({
                          ...b,
                          impuesto: e.target.value,
                        }))
                      }
                      placeholder="Impuesto"
                    />
                    <Input
                      aria-label="Sancion"
                      inputMode="decimal"
                      value={breakdown.sancion}
                      onChange={(e) =>
                        setBreakdown((b) => ({ ...b, sancion: e.target.value }))
                      }
                      placeholder="Sancion"
                    />
                    <Input
                      aria-label="Intereses"
                      inputMode="decimal"
                      value={breakdown.intereses}
                      onChange={(e) =>
                        setBreakdown((b) => ({
                          ...b,
                          intereses: e.target.value,
                        }))
                      }
                      placeholder="Intereses"
                    />
                  </div>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tax-payment-reference">Referencia</Label>
                  <Input
                    id="tax-payment-reference"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="PSE o banco"
                  />
                </div>
                {paymentFilesEvent ? (
                  <FilingFields
                    idPrefix="tax-payment-filing"
                    draft={paymentFiling}
                    onChange={setPaymentFiling}
                    required
                    hint="Esta declaracion aun no esta presentada: el pago tambien registra la presentacion, asi que necesita el numero de formulario y la fecha de presentacion."
                  />
                ) : null}
                <Button type="submit" disabled={addPayment.isPending}>
                  {addPayment.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  Registrar pago
                </Button>
              </form>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function FilingFields(props: {
  idPrefix: string;
  draft: FilingDraft;
  onChange: (draft: FilingDraft) => void;
  required: boolean;
  hint: string;
}) {
  const { idPrefix, draft, onChange, required, hint } = props;
  return (
    <div className="grid gap-3 rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{hint}</p>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-number`}>
          Numero de formulario{required ? "" : " (opcional)"}
        </Label>
        <Input
          id={`${idPrefix}-number`}
          value={draft.formNumber}
          onChange={(e) => onChange({ ...draft, formNumber: e.target.value })}
          placeholder="Como aparece en el formulario"
          required={required}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-date`}>
          Fecha de presentacion{required ? "" : " (opcional)"}
        </Label>
        <Input
          id={`${idPrefix}-date`}
          type="date"
          value={draft.filedAt}
          onChange={(e) => onChange({ ...draft, filedAt: e.target.value })}
          required={required}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-correction`}>
          Corrige el formulario No. (opcional)
        </Label>
        <Input
          id={`${idPrefix}-correction`}
          value={draft.correctionOf}
          onChange={(e) => onChange({ ...draft, correctionOf: e.target.value })}
          placeholder="Numero del formulario corregido"
        />
      </div>
    </div>
  );
}

function FilingsTable(props: {
  companyId: string;
  filings: TaxDeclarationRecord[];
}) {
  const fmtCop = useMoneyFormatter();
  const { companyId, filings } = props;
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function download(filing: TaxDeclarationRecord) {
    if (!filing.documentId) return;
    setDownloadingId(filing.id);
    try {
      saveBlob(
        await getDocumentContentBlob(companyId, filing.documentId),
        `formulario-${filing.formNumber}.pdf`,
      );
    } catch (err) {
      errorToast("No pudimos descargar el formulario", err);
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Formulario</TableHead>
          <TableHead>Presentado</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead>Origen</TableHead>
          <TableHead className="text-right">Declarado</TableHead>
          <TableHead className="text-right">Archivo</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {filings.map((filing) => (
          <TableRow key={filing.id}>
            <TableCell>
              <div className="font-medium">
                {filing.formCode ? `${filing.formCode} · ` : ""}No.{" "}
                {filing.formNumber}
              </div>
              {filing.correctionOf ? (
                <div className="text-xs text-muted-foreground">
                  corrige {filing.correctionOf}
                </div>
              ) : null}
              {filing.notes ? (
                <div className="text-xs text-muted-foreground">
                  {filing.notes}
                </div>
              ) : null}
            </TableCell>
            <TableCell>{fmtDate(filing.filedAt)}</TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1.5">
                <Badge
                  variant={
                    filing.recordKind === "filed_original"
                      ? "secondary"
                      : "outline"
                  }
                >
                  {filing.recordKind === "filed_original"
                    ? "Original"
                    : "Correccion"}
                </Badge>
                <Badge
                  variant={
                    filing.effectiveness === "filed" ? "success" : "warning"
                  }
                >
                  {filing.effectiveness === "filed"
                    ? "Presentado"
                    : "Eficacia sin verificar"}
                </Badge>
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {EVIDENCE_SOURCE_LABEL[filing.source]}
            </TableCell>
            <TableCell className="text-right">
              {fmtCop(filing.declaredAmount)}
            </TableCell>
            <TableCell className="text-right">
              {filing.documentId ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void download(filing)}
                  disabled={downloadingId === filing.id}
                >
                  {downloadingId === filing.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  Descargar
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Metric(props: { title: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs font-medium text-muted-foreground">{props.title}</p>
      <p className="mt-2 text-xl font-semibold">{props.value}</p>
    </div>
  );
}

function HelpList(props: { title: string; items: string[] }) {
  return (
    <div>
      <h2 className="text-sm font-semibold">{props.title}</h2>
      <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
        {props.items.map((item) => (
          <li key={item} className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
